/**
 * Timer actions + the per-browser alert preferences.
 *
 * Idle: pick a goal (plannable goals only) and Start. Focus: a two-step
 * Abort (an aborted focus counts nothing). Break: Skip break, or start the
 * next focus right away (the server skips the rest of the break). A note
 * records what the focus achieved — during a break it lands on the focus
 * that just ended.
 *
 * Preferences are local UI state (`useFocusPrefs`), not server data; the
 * notification permission is requested inside the click that turns it on.
 */
import { useEffect, useState } from "react";
import { clsx } from "clsx";

import type { TimerSnapshot } from "../../api/focus";
import { isBreak } from "../../lib/focus";
import { notificationPermission, requestNotifyPermission } from "../../lib/focusNotify";
import { primeChime } from "../../lib/focusSound";
import { formatMinutes } from "../../lib/focusTime";
import { draftKey, useDraft } from "../../lib/hooks/useDraft";
import { useFocusPrefs } from "../../lib/hooks/useFocusPrefs";

const ABORT_CONFIRM_MS = 4_000;

export interface StartGoal {
  id: string;
  title: string;
}

export function TimerControls({
  snapshot,
  goals,
  defaultGoalId,
  busy,
  onStart,
  onAbort,
  onSkip,
  onNote,
}: {
  snapshot: TimerSnapshot;
  goals: StartGoal[];
  defaultGoalId: string | null;
  busy: boolean;
  onStart: (goalId: string) => void;
  onAbort: (sessionId: string) => void;
  onSkip: (sessionId: string) => void;
  /** Resolves on success, rejects (keeping the draft) on failure. */
  onNote: (note: string, sessionId: string | undefined) => Promise<void>;
}) {
  const { phase, session, settings } = snapshot;
  const [picked, setPicked] = useState<string | null>(null);
  const goalId = picked && goals.some((g) => g.id === picked) ? picked : defaultGoalId;
  const [confirmAbort, setConfirmAbort] = useState(false);

  useEffect(() => {
    if (!confirmAbort) return;
    const t = window.setTimeout(() => setConfirmAbort(false), ABORT_CONFIRM_MS);
    return () => window.clearTimeout(t);
  }, [confirmAbort]);

  const canStart = phase !== "focus" && !!goalId && !busy;

  return (
    <div className="timer-controls">
      {phase !== "focus" ? (
        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex-1 basis-44 min-w-0">
            <label className="label" htmlFor="focus-start-goal">
              {isBreak(phase) ? "Next focus" : "Focus on"}
            </label>
            <select
              id="focus-start-goal"
              className="input"
              value={goalId ?? ""}
              disabled={goals.length === 0}
              onChange={(e) => setPicked(e.target.value || null)}
            >
              {goals.length === 0 ? <option value="">No plannable goal – add one under Goals</option> : null}
              {goals.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.title}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="btn btn-main"
            disabled={!canStart}
            onClick={() => goalId && onStart(goalId)}
          >
            Start focus · {formatMinutes(settings.focus_minutes)}
          </button>
          {isBreak(phase) && session ? (
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy}
              onClick={() => onSkip(session.id)}
            >
              Skip break
            </button>
          ) : null}
        </div>
      ) : session ? (
        <div className="flex items-center gap-2 flex-wrap">
          {confirmAbort ? (
            <>
              <button
                type="button"
                className="btn btn-sm btn-main"
                disabled={busy}
                onClick={() => {
                  setConfirmAbort(false);
                  onAbort(session.id);
                }}
              >
                Abort – this focus counts nothing
              </button>
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setConfirmAbort(false)}>
                Keep going
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              disabled={busy}
              onClick={() => setConfirmAbort(true)}
            >
              Abort
            </button>
          )}
        </div>
      ) : null}

      {session && phase !== "idle" ? (
        <SessionNote
          key={session.id}
          sessionId={session.id}
          forRunningFocus={phase === "focus"}
          onNote={onNote}
        />
      ) : null}

      <PrefToggles />
    </div>
  );
}

function SessionNote({
  sessionId,
  forRunningFocus,
  onNote,
}: {
  sessionId: string;
  forRunningFocus: boolean;
  onNote: (note: string, sessionId: string | undefined) => Promise<void>;
}) {
  const { draft, setDraft, clearDraft } = useDraft(draftKey("focus-note", sessionId));
  const [saving, setSaving] = useState(false);

  async function save() {
    const text = draft.trim();
    if (!text) return;
    setSaving(true);
    try {
      // During a break the note belongs to the focus that just ended — the
      // server's default target when no session id is given.
      await onNote(text, forRunningFocus ? sessionId : undefined);
      clearDraft();
    } catch {
      // The page toasts; the text stays for a retry.
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-4">
      <label className="label" htmlFor="focus-note">
        {forRunningFocus ? "Note for this focus" : "Note for the focus that just ended"}
      </label>
      <div className="flex items-start gap-2">
        <textarea
          id="focus-note"
          className="input font-mono text-sm"
          rows={2}
          maxLength={2000}
          placeholder="What did this focus move?"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <button
          type="button"
          className="btn btn-sm btn-second"
          disabled={saving || !draft.trim()}
          onClick={() => void save()}
        >
          Save
        </button>
      </div>
    </div>
  );
}

function PrefToggles() {
  const [prefs, setPrefs] = useFocusPrefs();
  const [denied, setDenied] = useState(false);
  const permission = notificationPermission();

  async function toggleNotify() {
    if (prefs.notify) {
      setPrefs({ notify: false });
      return;
    }
    const granted = await requestNotifyPermission();
    setDenied(!granted);
    if (granted) setPrefs({ notify: true });
  }

  function toggleSound() {
    if (!prefs.sound) primeChime();
    setPrefs({ sound: !prefs.sound });
  }

  return (
    <div className="timer-prefs">
      <div className="flex items-center gap-1.5 flex-wrap">
        <PrefButton
          on={prefs.notify}
          disabled={permission === "unsupported"}
          onClick={() => void toggleNotify()}
          label="Notify"
        />
        <PrefButton on={prefs.sound} onClick={toggleSound} label="Sound" />
        <PrefButton
          on={prefs.titleCountdown}
          onClick={() => setPrefs({ titleCountdown: !prefs.titleCountdown })}
          label="Tab title"
        />
      </div>
      <p className="timer-prefs-hint">
        {denied || (prefs.notify && permission === "denied")
          ? "The browser blocks notifications for this site – allow them in its site settings."
          : "Mac app notifies; enable here for browser-only use."}
      </p>
    </div>
  );
}

function PrefButton({
  on,
  label,
  onClick,
  disabled,
}: {
  on: boolean;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className={clsx("pref-toggle", on && "is-on")}
      aria-pressed={on}
      disabled={disabled}
      onClick={onClick}
    >
      <span className="pref-toggle-box" aria-hidden>
        {on ? "[x]" : "[ ]"}
      </span>
      {label}
    </button>
  );
}
